// Client-side condition evaluator for Form Flow branching (Batch 2b-i).
//
// Mirrors backend/services/condition_engine.py operators + coercion so the
// JSON shape stays canonical, but runs in the browser — a flow picks its next
// screen live, with no per-question server round-trip. Field references use the
// `answer.<question_id>` convention (see buildAnswerContext) for the legacy
// FormFlow schema. The new ConditionEditor schema uses raw field_id keys.

import { getNextStepIds, getStepTargetId, resolveEntryStepId, isForwarderId } from './workflowEdgeUtils';

// Normalize booleans to 'yes'/'no' so they match user-entered case values
// (e.g., a yes_no field stores true/false, but the user types "Yes" in a
// switch_case condition — toStr(true) must equal toStr("Yes") → "yes").
const toStr = (v) => {
    if (v === true) return 'yes';
    if (v === false) return 'no';
    if (v === null || v === undefined) return '';
    return String(v).toLowerCase().trim();
};
const toNum = (v) => { const n = Number(v); return Number.isNaN(n) ? 0 : n; };
const toList = (v) => Array.isArray(v) ? v : (typeof v === 'string' ? v.split(',').map(s => s.trim()) : []);
const isEmpty = (a) => a === null || a === undefined || a === '' || (Array.isArray(a) && a.length === 0);
const truthy = (a) => a === true || a === 1 || ['true', 'yes', '1', 'y'].includes(toStr(a));

function evalCondition(cond, ctx) {
    const actual = ctx[cond.field];
    const value = cond.value;
    switch (cond.operator) {
        case 'equals': return toStr(actual) === toStr(value);
        case 'not_equals': return toStr(actual) !== toStr(value);
        case 'contains':
            return Array.isArray(actual)
                ? actual.some(el => toStr(el).includes(toStr(value)))
                : toStr(actual).includes(toStr(value));
        case 'not_contains':
            return !(Array.isArray(actual)
                ? actual.some(el => toStr(el).includes(toStr(value)))
                : toStr(actual).includes(toStr(value)));
        case 'starts_with': return toStr(actual).startsWith(toStr(value));
        case 'ends_with': return toStr(actual).endsWith(toStr(value));
        case 'in_list': return toList(value).map(toStr).includes(toStr(actual));
        case 'not_in_list': return !toList(value).map(toStr).includes(toStr(actual));
        case 'greater_than': return toNum(actual) > toNum(value);
        case 'less_than': return toNum(actual) < toNum(value);
        case 'greater_equal': return toNum(actual) >= toNum(value);
        case 'less_equal': return toNum(actual) <= toNum(value);
        case 'is_empty': return isEmpty(actual);
        case 'is_not_empty': return !isEmpty(actual);
        case 'is_true': return truthy(actual);
        case 'is_false': return !truthy(actual);
        case 'exists': return actual !== undefined && actual !== null;
        case 'not_exists': return actual === undefined || actual === null;
        default:
            console.warn('[flowConditions] unknown operator:', cond.operator);
            return false;
    }
}

// Port of evaluate_condition_group. Empty group → true (matches Python; a rule
// with no conditions acts as a catch-all).
export function evaluateGroup(group, ctx) {
    if (!group) return true;
    const logic = group.logic || 'and';
    const results = [
        ...(group.conditions || []).map(c => evalCondition(c, ctx)),
        ...(group.groups || []).map(g => evaluateGroup(g, ctx)),
    ];
    if (results.length === 0) return true;
    return logic === 'or' ? results.some(Boolean) : results.every(Boolean);
}

// { question_id: value } → { 'answer.<question_id>': value }
export function buildAnswerContext(answers) {
    const ctx = {};
    Object.keys(answers || {}).forEach(k => { ctx[`answer.${k}`] = answers[k]; });
    return ctx;
}

export const FLOW_END = '__end__';

// ─── New-schema branch evaluator (ConditionEditor writes this shape) ─────────
//
// branch: {
//   mode: 'switch_case' | 'if_else',
//   source_field: '<field_id>',
//   cases:    [{ value, targetStepId }],                                  // switch_case
//   branches: [{ name, matchLogic: 'all'|'any',
//                conditions: [{ field, operator, value }], targetStepId }], // if_else
//   default_target: '<step_id>',
// }
//
// For the new schema, conditions[].field is a raw field_id, so we pass the
// raw answers object directly to evalCondition (NOT buildAnswerContext).
//
// Returns: target step_id string, FLOW_END, or undefined (no match → caller
// falls back to linear next).
export function evaluateNewBranch(branch, answers) {
    if (!branch || !branch.mode) return undefined;
    const ans = answers || {};

    if (branch.mode === 'switch_case') {
        const sourceVal = toStr(ans[branch.source_field]);
        for (const c of (branch.cases || [])) {
            if (sourceVal === toStr(c.value)) return c.targetStepId;
        }
        return branch.default_target || undefined;
    }

    if (branch.mode === 'if_else') {
        for (const b of (branch.branches || [])) {
            const results = (b.conditions || []).map(c => evalCondition(c, ans));
            const matched = b.matchLogic === 'any'
                ? results.some(Boolean)
                : results.every(Boolean);
            if (matched) return b.targetStepId;
        }
        return branch.default_target || undefined;
    }

    return undefined;
}

// ─── Router-step detection ───────────────────────────────────────────────────
// A step is a router if it has a branch object with either the new schema
// (branch.mode) or the legacy schema (Array.isArray(branch.rules)).
export function isRouterStep(step) {
    return !!(step && step.branch && (step.branch.mode || Array.isArray(step.branch.rules)));
}

// ─── Transitive step resolution ──────────────────────────────────────────────
// Resolve a target step_id to an index, skipping router steps transitively.
// When the target is itself a router, evaluate its branch and hop again.
// Guard with a visited-set to survive cycles.
//
// Returns: numeric index, FLOW_END, or undefined (stale/unknown/cycle → caller
// falls back to linear next).
function resolveStepId(steps, targetId, answers, visited, forwarders) {
    if (targetId === FLOW_END) return FLOW_END;
    if (!targetId) return undefined;

    const idx = steps.findIndex(s => s.step_id === targetId);

    if (idx === -1) {
        // Not found in steps — check if it's a forwarder (unified model).
        // Forwarders are fire-and-forget: the backend fires them on submit,
        // so the runtime just auto-advances to the forwarder's next_step_id.
        // MULTI-EDGE: next_step_id can be an array; use the first target.
        if (forwarders && forwarders.length > 0) {
            const fwd = forwarders.find(f => f.step_id === targetId);
            if (fwd) {
                if (visited.has(targetId)) return undefined; // cycle guard
                visited.add(targetId);
                const fwdNextIds = getNextStepIds(fwd);
                if (fwdNextIds.length > 0) {
                    const hop = getStepTargetId(fwd, forwarders) || fwdNextIds[0];
                    return resolveStepId(steps, hop, answers, visited, forwarders);
                }
                return FLOW_END; // forwarder with no next_step_id → end
            }
        }
        return undefined;   // stale/unknown target → linear next
    }

    if (visited.has(idx)) return undefined; // cycle → linear next
    visited.add(idx);

    const step = steps[idx];
    if (!isRouterStep(step)) return idx; // not a router — we're done

    // Evaluate the router's branch
    let nextTarget;
    if (step.branch.mode) {
        nextTarget = evaluateNewBranch(step.branch, answers);
    } else {
        // Legacy FormFlow rules
        const ctx = buildAnswerContext(answers);
        for (const rule of step.branch.rules) {
            if (evaluateGroup(rule.condition_group || {}, ctx)) { nextTarget = rule.go_to; break; }
        }
        if (nextTarget === undefined) nextTarget = step.branch.default_go_to;
    }

    if (nextTarget === FLOW_END) return FLOW_END;
    if (!nextTarget) {
        // No matching target → workflow ends (no linear-next fallback).
        // The user must configure a default_target on the router or the flow stops.
        return FLOW_END;
    }
    return resolveStepId(steps, nextTarget, answers, visited, forwarders);
}

// ─── Entry index ─────────────────────────────────────────────────────────────
// Index of the workflow entry step in `steps` (sorted renderer order).
// entry_step_id undefined → legacy fallback to the first step; null or a
// missing id → also the first step (a public form must render something; the
// canvas warns the author about the missing entry).
export function resolveEntryIndex(steps, formData) {
    if (!steps || steps.length === 0) return 0;
    const entryId = resolveEntryStepId(formData) || formData?.steps?.[0]?.step_id;
    const idx = steps.findIndex(s => s.step_id === entryId);
    return idx >= 0 ? idx : 0;
}

// ─── Resolve the initial step (handles router at the entry) ───────────────────
// Returns the index of the first non-router step, or FLOW_END.
export function resolveInitialStep(steps, answers, forwarders, entryIndex = 0) {
    if (!steps || steps.length === 0) return 0;
    const start = steps[entryIndex] ? entryIndex : 0;
    if (!isRouterStep(steps[start])) return start;

    const visited = new Set();
    visited.add(start);

    let target;
    if (steps[start].branch.mode) {
        target = evaluateNewBranch(steps[start].branch, answers);
    } else {
        const ctx = buildAnswerContext(answers);
        for (const rule of steps[start].branch.rules) {
            if (evaluateGroup(rule.condition_group || {}, ctx)) { target = rule.go_to; break; }
        }
        if (target === undefined) target = steps[start].branch.default_go_to;
    }

    if (target === FLOW_END) return FLOW_END;
    if (!target) return FLOW_END; // no match → workflow ends (no linear-next fallback)

    const resolved = resolveStepId(steps, target, answers, visited, forwarders);
    if (resolved === FLOW_END) return FLOW_END;
    return resolved === undefined ? FLOW_END : resolved; // stale/unknown/cycle → end
}

// Decide the screen index to show after `currentIndex`, given answers-so-far.
// Returns a numeric index or FLOW_END. Resolves a step's branch rules in order
// (first match → target), else default_target, else the linear next screen.
// Target references a step_id; an unknown/stale target falls back to linear next.
// Router steps are skipped transitively (never landed on).
export function nextIndex(screens, currentIndex, answers, forwarders) {
    const step = screens[currentIndex];
    const branch = step && step.branch;
    let target; // step_id | FLOW_END | undefined

    // Legacy path: branch.rules (old FormFlow schema)
    if (branch && Array.isArray(branch.rules)) {
        const ctx = buildAnswerContext(answers);
        for (const rule of branch.rules) {
            if (evaluateGroup(rule.condition_group || {}, ctx)) { target = rule.go_to; break; }
        }
        if (target === undefined) target = branch.default_go_to;
    }
    // New schema path: branch.mode (switch_case / if_else from ConditionEditor)
    else if (branch && branch.mode) {
        target = evaluateNewBranch(branch, answers);
    }

    // Non-condition steps may have a next_step_id override (set when the user
    // manually draws an edge on the canvas). This is the ONLY way a non-condition
    // step can advance — there is no linear-next fallback. If a step has no
    // next_step_id and no branch, the workflow ends here (FLOW_END).
    // EDGE RULE: next_step_id holds at most ONE step target plus forwarder
    // targets. The step target drives the visible flow regardless of its
    // position in the array (a stale id used to sit first and end the flow);
    // forwarders are fired server-side. A node whose only targets are
    // forwarders follows the forwarder chain (resolveStepId hops through it).
    if (!branch && step && step.next_step_id) {
        const nextIds = getNextStepIds(step);
        if (nextIds.length > 0) {
            // Prefer a step target that actually exists (a published document
            // saved before dangling ids were pruned may still carry one), then
            // any step target, then the forwarder chain.
            const stepIds = nextIds.filter(id => !isForwarderId(id, forwarders));
            target = stepIds.find(id => screens.some(s => s.step_id === id))
                || getStepTargetId(step, forwarders)
                || nextIds[0];
        }
    }

    if (target === FLOW_END) return FLOW_END;
    if (target === undefined || target === null || target === '') return FLOW_END;

    // Resolve the target, skipping router steps transitively.
    // Forwarders are also resolved (auto-advanced past) in the unified model.
    const visited = new Set([currentIndex]);
    const resolved = resolveStepId(screens, target, answers, visited, forwarders);
    if (resolved === FLOW_END) return FLOW_END;
    if (resolved === undefined) return FLOW_END; // stale/unknown/cycle → end
    return resolved;
}

// ─── Terminal detection for the Next/Submit button ───────────────────────────
// True when advancing from `currentIndex` submits the form: no next step, an
// End step, an end page, or a redirect that has nothing after it.
export function isTerminalAdvance(screens, currentIndex, answers, forwarders) {
    const next = nextIndex(screens, currentIndex, answers, forwarders);
    if (next === FLOW_END) return true;
    const step = screens[next];
    if (!step) return true;
    if (step.kind === 'end' || step.kind === 'end_page') return true;
    if (step.kind === 'redirect') {
        return nextIndex(screens, next, answers, forwarders) === FLOW_END;
    }
    return false;
}
